import { Typography, Box, Paper } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import { ChatClient } from '../components/chat/ChatClient';
import type { ChatClientRef } from '../components/chat/ChatClient';
import { FormModal } from '../components/chat/FormModal';
import { SubmissionStatusPanel } from '../components/chat/SubmissionStatusPanel';
import type { ChatAction } from '@lingaai/types';

export function AdminDashboard() {
  const location = useLocation();
  const { data: user } = useCurrentUser();
  const { data: tenant } = useTenant(user?.tenant_id || '');
  const [currentAction, setCurrentAction] = useState<ChatAction | null>(null);
  const chatClientRef = useRef<ChatClientRef>(null);

  // Handle initial message from navigation state
  useEffect(() => {
    if (location.state?.message && chatClientRef.current) {
      chatClientRef.current.notifyAgent(location.state.message);
      // Clear the state to prevent re-sending on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  if (!user || !tenant) {
    return <Typography>Loading...</Typography>;
  }

  const drawerWidth = '50%';
  const drawerOpen = currentAction?.type === 'show_form' || currentAction?.type === 'show_status';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box sx={{ display: 'flex', gap: 2, flex: 1, overflow: 'hidden', px: 3, pb: 3 }}>
        {/* Manager Agent Chat */}
        <Paper
          sx={{
            p: 3,
            flex: 1,
            minWidth: 0,
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Chat
          </Typography>
          <ChatClient ref={chatClientRef} tenantId={user.tenant_id || ''} userId={user.id} userName={(user.full_name || user.email || 'User') as string} onActionTriggered={setCurrentAction} />
        </Paper>

        {/* Side Panel (not a Drawer) */}
        {drawerOpen && (
          <Paper
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              transition: 'all 0.3s ease',
              overflow: 'hidden',
            }}
          >
            {currentAction?.type === 'show_form' && (
              <FormModal
                key={`${currentAction.form_id}-${Date.now()}`}
                formId={currentAction.form_id!}
                data={{
                  ...currentAction.data,
                  ...(currentAction.form_id === 'upload_document' && {
                    file: chatClientRef.current?.getUploadedFile(),
                  }),
                }}
                tenantId={user.tenant_id || ''}
                onSuccess={(result: any) => {
                  // Handle form transitions (e.g., trial_processing -> trial_results)
                  if (result.action === 'show_results' && result.data) {
                    setCurrentAction({
                      type: 'show_form',
                      form_id: 'trial_results',
                      data: result.data,
                    });
                    return; // Don't close the form, just transition
                  }

                  // Notify agent based on form type
                  if (currentAction.form_id === 'define_metrics') {
                    chatClientRef.current?.notifyAgent(`I just saved ${result} metric definitions for the fund.`);
                  } else if (currentAction.form_id === 'add_fund') {
                    chatClientRef.current?.notifyAgent(`I just created a new fund. What should I do next?`);
                  } else if (currentAction.form_id === 'import_companies') {
                    chatClientRef.current?.notifyAgent(`I just imported ${result.companies} companies and ${result.contacts} contacts.`);
                  } else if (currentAction.form_id === 'upload_document') {
                    const link = `/funds/${result.fundId}?submission=${result.submissionId}`;
                    chatClientRef.current?.notifyAgent(`Processing complete for ${result.companyName} ${result.period}. [View submission](${link})`);
                  } else if (currentAction.form_id === 'trial_upload') {
                    // Notify agent with uploaded file metadata from trial
                    const fileMetadata = {
                      fileName: result.uploadedFile.fileName,
                      filePath: result.uploadedFile.filePath,
                      fileSize: result.uploadedFile.fileSize,
                      mimeType: result.uploadedFile.mimeType,
                    };
                    chatClientRef.current?.notifyAgent(`I uploaded a trial document: ${result.uploadedFile.fileName}`, fileMetadata);
                  } else if (currentAction.form_id === 'trial_confirmation' || currentAction.form_id === 'show_trial_confirmation') {
                    // User confirmed trial extraction
                    chatClientRef.current?.notifyAgent(`Run extraction for submission ${result.submissionId}`);
                  } else if (currentAction.form_id === 'trial_results') {
                    // User wants to set up their fund after seeing trial results
                    if (result.action === 'setup_fund') {
                      chatClientRef.current?.notifyAgent('I want to set up my fund');
                    }
                  } else if (currentAction.form_id === 'trial_processing') {
                    // Processing completed, form already transitioned to results
                    // Do nothing - the form already handled the transition
                    return;
                  }
                  setCurrentAction(null);
                }}
                onClose={() => setCurrentAction(null)}
              />
            )}
            {currentAction?.type === 'show_status' && (
              <SubmissionStatusPanel
                fundName={currentAction.data?.fund_name || 'Fund'}
                period={currentAction.data?.period || ''}
                submissions={currentAction.data?.submissions || []}
                onClose={() => setCurrentAction(null)}
              />
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
}
